import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController, NavParams } from '@ionic/angular/standalone';
import { fieldSorter } from '@scandium-oy/ngx-scandium';
import { filter, map, shareReplay } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { MainDialogComponent } from 'src/app/components/main-dialog/main-dialog.component';
import { Client } from 'src/app/models/client.model';
import { SelectDialogComponent } from 'src/app/select-dialog/select.dialog';
import { ClientsService } from 'src/app/services/clients.service';
import { ContractorsService } from 'src/app/services/contractors.service';
import { SitesService } from 'src/app/services/sites.service';
import { UsersService } from 'src/app/services/users.service';
import { adminActionRoles, commercialAdminRoles, companyAdmins, contractorRoles, managerAdminRoles, Roles } from 'src/app/utility/role';
import { capitalizeFirstLetter } from 'src/app/utility/string';

@Component({
  selector: 'app-invite-user-dialog',
  templateUrl: './invite-user.dialog.html',
  styleUrls: ['./invite-user.dialog.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    MainDialogComponent,
  ],
})
export class InviteUserDialogComponent {
  private clientsService = inject(ClientsService);
  private contractorsService = inject(ContractorsService);

  private sites$ = this.sitesService.getAdminList();

  readonly adminActionRoles = adminActionRoles;
  readonly contractorRoles = contractorRoles;

  formGroup: FormGroup;

  canBeDummy = signal(false);
  roleS = signal<Roles>(null);
  roles = computed(() => {
    const role = this.userRoleS();
    if (role === Roles.contractorAdmin) {
      return contractorRoles;
    } else if (role === Roles.admin) {
      return managerAdminRoles;
    } else if (role === Roles.commercialAdmin) {
      return commercialAdminRoles;
    } else if (role === Roles.superAdmin) {
      return [Roles.partner];
    }
  });

  allWorksites$ = this.sites$.pipe(
    filter((sites) => sites.length > 0),
    map((sites) => sites.map((site) => {
      site.clientName = capitalizeFirstLetter(site.client);
      return { name: site.name, clientName: site.clientName, guid: site.guid };
    })),
    map((sites) => sites.sort(fieldSorter(['clientName', 'name']))),
    shareReplay(1),
  );

  clients$ = this.clientsService.getList();

  userRoleS = computed(() => this.usersService.currentUserS()?.role);

  constructor(
    private formBuilder: FormBuilder,
    private _modal: ModalController,
    private modalCtrl: ModalController,
    private sitesService: SitesService,
    private usersService: UsersService,
    navParams: NavParams,
  ) {
    this.roleS.set(navParams.get('role') ?? null);
    const worksite = navParams.get('site');
    this.canBeDummy.set(navParams.get('dummy') ?? false);

    this.formGroup = this.formBuilder.group({
      displayName: ['', Validators.required],
      role: [this.roleS(), Validators.required],
      email: ['', [Validators.required, Validators.email]],
      worksites: [worksite ? [{ guid: worksite }] : []],
      phone: [''],
      address: this.formBuilder.group({
        street: [''],
        postalCode: [''],
        city: [''],
      }),
      clients: [[]],
      rights: this.formBuilder.group({
        officer: [false],
        calendar: [false],
      }),
    });
  }

  saveContact() {
    const user = this.formGroup.value;

    user.clients = [this.clientsService.clientS()?.guid];
    user.worksites = user.worksites?.map((it) => it.guid) ?? [];
    user.disabled = true;
    this._modal.dismiss(user);
  }

  selectClient(items: Client[]) {
    const clearButton = this.formGroup.get('clients').value != null;
    this.modalCtrl.create({ component: SelectDialogComponent, componentProps: { items, clearButton }, cssClass: ['stack-modal'] }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          if (data.data.clear) {
            this.formGroup.get('clients').setValue(null);
          } else {
            this.formGroup.get('clients').setValue([]);
          }
        }
      });
    });
  }

  onSave() {
    const user = this.formGroup.value;
    if (companyAdmins.includes(this.userRoleS())) {
      user.clients = [this.clientsService.clientS()?.guid];
    } else if ([Roles.contractorAdmin].includes(this.userRoleS())) {
      user.contractors = [this.contractorsService.contractorS().guid];
    } else if (this.userRoleS() === Roles.superAdmin && user.role === Roles.partner) {
      user.rights = {
        premiumPartner: true,
      };
    }
    user.worksites = user.worksites?.map((it) => it.guid) ?? [];
    this._modal.dismiss(user);
  }

  dismiss() {
    this._modal.dismiss();
  }
}
